import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { ArrowLeft, Check, Lock, ChevronRight, FileDown, Plus, X, ShieldCheck, History } from 'lucide-react';
import { toast } from 'sonner';
import { PhotoUploader, FURNITURE_STAGES, FURNITURE_STAGE_LABELS, FURNITURE_STAGE_COLORS } from '../../components/os/FurnitureShared';

const Field = ({ label, children }) => (
  <div>
    <label className="block text-xs font-medium tracking-wider uppercase mb-1.5" style={{ color: '#C9B8A8' }}>{label}</label>
    {children}
  </div>
);

const inputCls = 'w-full px-3 py-2.5 rounded-lg text-sm outline-none';
const inputStyle = { backgroundColor: '#FAF9F6', border: '1px solid #E8E3D8', color: '#2D2D2D' };

const Card = ({ title, badge, children, testId }) => (
  <div data-testid={testId} className="rounded-xl border p-6" style={{ backgroundColor: '#FFFFFF', borderColor: '#E8E3D8' }}>
    <div className="flex items-center justify-between mb-4">
      <h2 className="text-lg font-light" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#2D2D2D' }}>{title}</h2>
      {badge}
    </div>
    {children}
  </div>
);

const SaveBtn = ({ saving, children = 'Guardar', testId }) => (
  <button data-testid={testId} type="submit" disabled={saving}
    className="px-5 py-2.5 rounded-lg text-sm font-medium tracking-wide transition-all disabled:opacity-50"
    style={{ backgroundColor: '#2D2D2D', color: '#FAF9F6' }}>
    {saving ? 'Guardando...' : children}
  </button>
);

export default function OSFurnitureDetail() {
  const { id, fid } = useParams();
  const { authFetch, user } = useAuth();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  const [showFichaForm, setShowFichaForm] = useState(false);
  const [forms, setForms] = useState({});

  const load = useCallback(async () => {
    try {
      const res = await authFetch(`/api/furniture/${fid}`);
      if (!res.ok) throw new Error();
      const data = await res.json();
      setItem(data);
      setForms({
        levantamiento: { ancho_cm: '', alto_cm: '', fondo_cm: '', restricciones: '', responsable: user?.name || '', fecha: new Date().toISOString().slice(0, 10), fotos: [], ...data.levantamiento },
        ficha: { medidas: { ancho_cm: '', alto_cm: '', fondo_cm: '' }, materiales: '', acabados: '', herrajes: '', requerimientos_uso: '', distribucion_interior: [], adjuntos: [] },
        cotizacion: { monto: '', tiempo_fabricacion: '', notas: '', evidencias: [], ...data.cotizacion_carpintero },
        presupuesto: { precio_venta: '', condiciones_pago: '', vigencia: '', ...data.presupuesto_cliente },
        anticipo: { monto: '', porcentaje: '', fecha: new Date().toISOString().slice(0, 10), comprobantes: [], ...data.anticipo },
        fabricacion: { fecha_inicio: '', fecha_estimada_entrega: '', ...data.fabricacion },
        avance: { nota: '', fotos: [] },
        instalacion: { fotos: [], ...data.instalacion },
      });
    } catch { navigate(`/os/projects/${id}`); }
    setLoading(false);
  }, [fid]);

  useEffect(() => { load(); }, [load]);

  const setF = (key, patch) => setForms(f => ({ ...f, [key]: { ...f[key], ...patch } }));

  const saveSection = async (section, payload, msg = 'Guardado') => {
    setSaving(true);
    try {
      const res = await authFetch(`/api/furniture/${fid}/section/${section}`, { method: 'PUT', body: JSON.stringify(payload) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      setItem(data);
      toast.success(msg);
    } catch (e) { toast.error(e.message || 'Error al guardar'); }
    setSaving(false);
  };

  const advance = async () => {
    setSaving(true);
    try {
      const res = await authFetch(`/api/furniture/${fid}/advance`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      setItem(data);
      toast.success(`Etapa avanzada a: ${data.stage_label}`);
    } catch (e) { toast.error(e.message || 'No se puede avanzar'); }
    setSaving(false);
  };

  const createFicha = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await authFetch(`/api/furniture/${fid}/ficha`, { method: 'POST', body: JSON.stringify(forms.ficha) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      setItem(data);
      setShowFichaForm(false);
      setF('ficha', { medidas: { ancho_cm: '', alto_cm: '', fondo_cm: '' }, materiales: '', acabados: '', herrajes: '', requerimientos_uso: '', distribucion_interior: [], adjuntos: [] });
      toast.success(`Ficha técnica v${data.ficha_versions.length} creada`);
      if (data.aprobacion_cliente?.invalidada) toast.warning('La aprobación del cliente quedó invalidada. Se requiere nueva aprobación.');
    } catch (e) { toast.error(e.message || 'Error'); }
    setSaving(false);
  };

  const downloadPdf = async () => {
    try {
      const res = await authFetch(`/api/furniture/${fid}/ficha-pdf`);
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail); }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ficha_carpintero_v${item.ficha_versions.length}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Ficha para carpintero generada (sin precios)');
    } catch (e) { toast.error(e.message || 'Error al generar PDF'); }
  };

  const simpleAction = async (url, body, msg) => {
    setSaving(true);
    try {
      const res = await authFetch(url, { method: 'POST', body: JSON.stringify(body || {}) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      setItem(data);
      toast.success(msg);
    } catch (e) { toast.error(e.message || 'Error'); }
    setSaving(false);
  };

  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: '#C9A66B', borderTopColor: 'transparent' }} /></div>;
  if (!item) return null;

  const stageIdx = item.stage_index;
  const isAdmin = user?.role === 'admin';
  const stageColor = FURNITURE_STAGE_COLORS[item.stage];
  const isStage = (s) => item.stage === s;
  const donePast = (s) => FURNITURE_STAGES.indexOf(s) < stageIdx;
  const vigente = item.ficha_versions?.length ? item.ficha_versions[item.ficha_versions.length - 1] : null;

  const dist = forms.ficha?.distribucion_interior || [];
  const setDist = (rows) => setF('ficha', { distribucion_interior: rows });

  return (
    <div data-testid="furniture-detail" className="space-y-6">
      <button onClick={() => navigate(`/os/projects/${id}`)} className="flex items-center gap-2 text-sm font-light" style={{ color: '#C9B8A8' }}>
        <ArrowLeft size={16} /> Volver al proyecto
      </button>

      {/* Header + Stepper */}
      <div className="rounded-xl border p-6" style={{ backgroundColor: '#FFFFFF', borderColor: '#E8E3D8' }}>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-5">
          <div>
            <h1 className="text-2xl font-light tracking-tight" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#2D2D2D' }}>{item.nombre}</h1>
            <p className="text-xs mt-1" style={{ color: '#C9B8A8' }}>Solicitado por {item.solicitud?.solicitante} · {item.solicitud?.fecha}</p>
          </div>
          <div className="flex items-center gap-2">
            {item.alert && <span data-testid="furniture-alert" className="text-xs px-3 py-1.5 rounded-full" style={{ backgroundColor: '#B87B7B20', color: '#B87B7B' }}>{item.alert}</span>}
            <span data-testid="furniture-stage-chip" className="text-xs px-3 py-1.5 rounded-full font-medium" style={{ backgroundColor: stageColor + '20', color: stageColor }}>{item.stage_label}</span>
          </div>
        </div>
        <div className="overflow-x-auto pb-1">
          <div className="flex items-center gap-1 min-w-max">
            {FURNITURE_STAGES.map((s, i) => {
              const done = i < stageIdx, active = i === stageIdx;
              const hardLock = (s === 'anticipo' && !item.aprobacion_cliente?.aprobado) || (s === 'fabricacion' && !item.anticipo?.monto);
              const color = done ? '#8B9F8D' : active ? (FURNITURE_STAGE_COLORS[s] || '#C9A66B') : '#C9B8A8';
              return (
                <React.Fragment key={s}>
                  <div data-testid={`furniture-stage-${s}`} className="flex items-center gap-1 text-[10px] px-2.5 py-1.5 rounded-full whitespace-nowrap"
                    style={{ backgroundColor: done || active ? color + '22' : '#F5F3EF', color: done || active ? color : '#C9B8A8', border: active ? `1.5px solid ${color}` : '1px solid transparent' }}>
                    {done ? <Check size={10} /> : (!active && hardLock ? <Lock size={10} /> : null)}
                    {FURNITURE_STAGE_LABELS[s]}
                  </div>
                  {i < FURNITURE_STAGES.length - 1 && <ChevronRight size={11} style={{ color: '#E8E3D8' }} />}
                </React.Fragment>
              );
            })}
          </div>
        </div>
        {stageIdx < FURNITURE_STAGES.length - 1 && (
          <div className="mt-4 pt-4 border-t flex justify-end" style={{ borderColor: '#E8E3D8' }}>
            <button data-testid="advance-stage-btn" onClick={advance} disabled={saving}
              className="px-5 py-2 rounded-lg text-sm font-medium tracking-wide transition-all disabled:opacity-50"
              style={{ backgroundColor: '#C9A66B', color: '#fff' }}>
              Avanzar a {FURNITURE_STAGE_LABELS[FURNITURE_STAGES[stageIdx + 1]]} →
            </button>
          </div>
        )}
      </div>

      {/* 1. SOLICITUD */}
      <Card title="1 · Solicitud" testId="section-solicitud" badge={donePast('solicitud') && <Check size={16} style={{ color: '#8B9F8D' }} />}>
        <p className="text-sm font-light mb-2" style={{ color: '#2D2D2D' }}>{item.solicitud?.descripcion}</p>
        <p className="text-xs" style={{ color: '#C9B8A8' }}>Solicitante: {item.solicitud?.solicitante} · Fecha: {item.solicitud?.fecha}</p>
        {item.solicitud?.fotos?.length > 0 && (
          <div className="flex gap-2 mt-3 flex-wrap">
            {item.solicitud.fotos.map(p => p.thumb_url && (
              <a key={p.id} href={`${process.env.REACT_APP_BACKEND_URL}${p.url}`} target="_blank" rel="noreferrer">
                <img src={`${process.env.REACT_APP_BACKEND_URL}${p.thumb_url}`} alt="" loading="lazy" className="w-20 h-20 rounded-lg object-cover border" style={{ borderColor: '#E8E3D8' }} />
              </a>
            ))}
          </div>
        )}
      </Card>

      {/* 2. LEVANTAMIENTO */}
      {stageIdx >= 1 && (
        <Card title="2 · Levantamiento en Sitio" testId="section-levantamiento" badge={donePast('levantamiento') && <Check size={16} style={{ color: '#8B9F8D' }} />}>
          <form onSubmit={e => { e.preventDefault(); saveSection('levantamiento', forms.levantamiento, 'Levantamiento guardado'); }} className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <Field label="Ancho (cm)"><input data-testid="lev-ancho" type="number" step="0.1" required value={forms.levantamiento.ancho_cm} onChange={e => setF('levantamiento', { ancho_cm: e.target.value })} className={inputCls} style={inputStyle} /></Field>
              <Field label="Alto (cm)"><input data-testid="lev-alto" type="number" step="0.1" required value={forms.levantamiento.alto_cm} onChange={e => setF('levantamiento', { alto_cm: e.target.value })} className={inputCls} style={inputStyle} /></Field>
              <Field label="Fondo (cm)"><input data-testid="lev-fondo" type="number" step="0.1" required value={forms.levantamiento.fondo_cm} onChange={e => setF('levantamiento', { fondo_cm: e.target.value })} className={inputCls} style={inputStyle} /></Field>
            </div>
            <Field label="Restricciones (tuberías, contactos, sifón...)">
              <textarea data-testid="lev-restricciones" rows={2} value={forms.levantamiento.restricciones || ''} onChange={e => setF('levantamiento', { restricciones: e.target.value })} className={`${inputCls} resize-none`} style={inputStyle} />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Responsable"><input data-testid="lev-responsable" required value={forms.levantamiento.responsable || ''} onChange={e => setF('levantamiento', { responsable: e.target.value })} className={inputCls} style={inputStyle} /></Field>
              <Field label="Fecha"><input data-testid="lev-fecha" type="date" required value={forms.levantamiento.fecha || ''} onChange={e => setF('levantamiento', { fecha: e.target.value })} className={inputCls} style={inputStyle} /></Field>
            </div>
            <PhotoUploader furnitureId={fid} photos={forms.levantamiento.fotos} onChange={fotos => setF('levantamiento', { fotos })} label="Fotos del sitio" testId="lev-photos" />
            <SaveBtn saving={saving} testId="lev-save-btn" />
          </form>
        </Card>
      )}

      {/* 3. FICHA TÉCNICA */}
      {stageIdx >= 2 && (
        <Card title="3 · Ficha Técnica de Diseño" testId="section-ficha"
          badge={
            <div className="flex items-center gap-2">
              {vigente && (
                <button data-testid="carpenter-pdf-btn" onClick={downloadPdf} className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border transition-colors" style={{ borderColor: '#C9A66B', color: '#C9A66B' }}>
                  <FileDown size={13} /> Ficha para carpintero (PDF)
                </button>
              )}
              <button data-testid="new-ficha-btn" onClick={() => setShowFichaForm(!showFichaForm)} className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg" style={{ backgroundColor: '#2D2D2D', color: '#FAF9F6' }}>
                <Plus size={13} /> Nueva versión
              </button>
            </div>
          }>
          {item.aprobacion_cliente?.aprobado && (
            <p className="text-xs mb-3 px-3 py-2 rounded-lg" style={{ backgroundColor: '#B87B7B15', color: '#B87B7B' }}>
              ⚠ La ficha está aprobada por el cliente. Crear una nueva versión invalidará la aprobación y habrá que reenviar al cliente.
            </p>
          )}
          {item.ficha_versions?.length === 0 && !showFichaForm && <p className="text-sm font-light" style={{ color: '#C9B8A8' }}>Aún no hay versiones. Crea la v1.</p>}

          {item.ficha_versions?.map(v => (
            <div key={v.version} data-testid={`ficha-v${v.version}`} className="rounded-lg border p-4 mb-3" style={{ borderColor: v.estado === 'vigente' ? '#C9A66B' : '#E8E3D8', opacity: v.estado === 'vigente' ? 1 : 0.6 }}>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium" style={{ color: '#2D2D2D' }}>v{v.version} <span className="text-xs font-light" style={{ color: v.estado === 'vigente' ? '#8B9F8D' : '#C9B8A8' }}>({v.estado})</span></p>
                <p className="text-[10px]" style={{ color: '#C9B8A8' }}>{v.created_by} · {v.created_at?.slice(0, 10)}</p>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs" style={{ color: '#2D2D2D' }}>
                <span>Medidas: {v.medidas?.ancho_cm}×{v.medidas?.alto_cm}×{v.medidas?.fondo_cm} cm</span>
                <span>Materiales: {v.materiales || '-'}</span>
                <span>Acabados: {v.acabados || '-'}</span>
                <span>Herrajes: {v.herrajes || '-'}</span>
              </div>
              {v.distribucion_interior?.length > 0 && (
                <div className="mt-2 text-xs" style={{ color: '#2D2D2D' }}>
                  <p className="font-medium mb-1" style={{ color: '#C9A66B' }}>Distribución interior:</p>
                  {v.distribucion_interior.map((d, i) => (
                    <p key={i} className="font-light">· {d.elemento}: {d.descripcion} ({d.ancho_util_cm}cm × {d.alto_util_cm}cm útiles)</p>
                  ))}
                </div>
              )}
              <p className="text-xs mt-2 font-light italic" style={{ color: '#8a6d3b' }}>Uso del cliente: "{v.requerimientos_uso}"</p>
            </div>
          ))}

          {showFichaForm && (
            <form onSubmit={createFicha} className="space-y-4 rounded-lg border border-dashed p-4 mt-2" style={{ borderColor: '#C9A66B' }}>
              <div className="grid grid-cols-3 gap-3">
                <Field label="Ancho final (cm)"><input data-testid="ficha-ancho" type="number" step="0.1" required value={forms.ficha.medidas.ancho_cm} onChange={e => setF('ficha', { medidas: { ...forms.ficha.medidas, ancho_cm: e.target.value } })} className={inputCls} style={inputStyle} /></Field>
                <Field label="Alto final (cm)"><input data-testid="ficha-alto" type="number" step="0.1" required value={forms.ficha.medidas.alto_cm} onChange={e => setF('ficha', { medidas: { ...forms.ficha.medidas, alto_cm: e.target.value } })} className={inputCls} style={inputStyle} /></Field>
                <Field label="Fondo final (cm)"><input data-testid="ficha-fondo" type="number" step="0.1" required value={forms.ficha.medidas.fondo_cm} onChange={e => setF('ficha', { medidas: { ...forms.ficha.medidas, fondo_cm: e.target.value } })} className={inputCls} style={inputStyle} /></Field>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <Field label="Materiales"><input data-testid="ficha-materiales" value={forms.ficha.materiales} onChange={e => setF('ficha', { materiales: e.target.value })} className={inputCls} style={inputStyle} /></Field>
                <Field label="Acabados"><input data-testid="ficha-acabados" value={forms.ficha.acabados} onChange={e => setF('ficha', { acabados: e.target.value })} className={inputCls} style={inputStyle} /></Field>
                <Field label="Herrajes"><input data-testid="ficha-herrajes" value={forms.ficha.herrajes} onChange={e => setF('ficha', { herrajes: e.target.value })} className={inputCls} style={inputStyle} /></Field>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs font-medium tracking-wider uppercase" style={{ color: '#C9B8A8' }}>Distribución interior</label>
                  <button type="button" data-testid="add-dist-row" onClick={() => setDist([...dist, { elemento: 'Cajón', descripcion: '', ancho_util_cm: '', alto_util_cm: '' }])}
                    className="text-xs" style={{ color: '#C9A66B' }}>+ Agregar elemento</button>
                </div>
                {dist.map((d, i) => (
                  <div key={i} className="grid grid-cols-[100px_1fr_80px_80px_28px] gap-2 mb-2 items-center">
                    <select value={d.elemento} onChange={e => setDist(dist.map((r, j) => j === i ? { ...r, elemento: e.target.value } : r))} className="px-2 py-2 rounded-lg text-xs outline-none" style={inputStyle}>
                      {['Cajón', 'Entrepaño', 'Barra', 'Puerta', 'Espacio', 'Otro'].map(o => <option key={o}>{o}</option>)}
                    </select>
                    <input placeholder="Descripción" value={d.descripcion} onChange={e => setDist(dist.map((r, j) => j === i ? { ...r, descripcion: e.target.value } : r))} className="px-2 py-2 rounded-lg text-xs outline-none" style={inputStyle} />
                    <input placeholder="Ancho útil" type="number" step="0.1" value={d.ancho_util_cm} onChange={e => setDist(dist.map((r, j) => j === i ? { ...r, ancho_util_cm: e.target.value } : r))} className="px-2 py-2 rounded-lg text-xs outline-none" style={inputStyle} />
                    <input placeholder="Alto útil" type="number" step="0.1" value={d.alto_util_cm} onChange={e => setDist(dist.map((r, j) => j === i ? { ...r, alto_util_cm: e.target.value } : r))} className="px-2 py-2 rounded-lg text-xs outline-none" style={inputStyle} />
                    <button type="button" onClick={() => setDist(dist.filter((_, j) => j !== i))} style={{ color: '#B87B7B' }}><X size={14} /></button>
                  </div>
                ))}
              </div>
              <Field label="Requerimientos de uso del cliente (obligatorio)">
                <textarea data-testid="ficha-requerimientos" rows={3} required value={forms.ficha.requerimientos_uso} onChange={e => setF('ficha', { requerimientos_uso: e.target.value })}
                  className={`${inputCls} resize-none`} style={inputStyle} placeholder="En palabras del cliente: qué necesita guardar o qué uso le dará al mueble..." />
              </Field>
              <PhotoUploader furnitureId={fid} photos={forms.ficha.adjuntos} onChange={adjuntos => setF('ficha', { adjuntos })} label="Render o dibujo (opcional)" accept="image/*,.pdf" testId="ficha-adjuntos" />
              <SaveBtn saving={saving} testId="ficha-save-btn">Crear versión v{(item.ficha_versions?.length || 0) + 1}</SaveBtn>
            </form>
          )}
        </Card>
      )}

      {/* 4. COTIZACIÓN CARPINTERO */}
      {stageIdx >= 3 && (
        <Card title="4 · Cotización del Carpintero" testId="section-cotizacion" badge={donePast('cotizacion_carpintero') && <Check size={16} style={{ color: '#8B9F8D' }} />}>
          <p className="text-xs mb-4 font-light" style={{ color: '#C9B8A8' }}>Genera la ficha PDF (botón arriba en Ficha Técnica), envíala por WhatsApp al carpintero y captura aquí su cotización.</p>
          <form onSubmit={e => { e.preventDefault(); saveSection('cotizacion_carpintero', forms.cotizacion, 'Cotización guardada'); }} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Monto cotizado (MXN)"><input data-testid="cot-monto" type="number" step="0.01" required value={forms.cotizacion.monto} onChange={e => setF('cotizacion', { monto: e.target.value })} className={inputCls} style={inputStyle} /></Field>
              <Field label="Tiempo de fabricación"><input data-testid="cot-tiempo" required placeholder="Ej: 3 semanas" value={forms.cotizacion.tiempo_fabricacion} onChange={e => setF('cotizacion', { tiempo_fabricacion: e.target.value })} className={inputCls} style={inputStyle} /></Field>
            </div>
            <Field label="Notas"><textarea data-testid="cot-notas" rows={2} value={forms.cotizacion.notas || ''} onChange={e => setF('cotizacion', { notas: e.target.value })} className={`${inputCls} resize-none`} style={inputStyle} /></Field>
            <PhotoUploader furnitureId={fid} photos={forms.cotizacion.evidencias} onChange={evidencias => setF('cotizacion', { evidencias })} label="Evidencia (captura o PDF del carpintero)" accept="image/*,.pdf" testId="cot-evidencias" />
            <SaveBtn saving={saving} testId="cot-save-btn" />
          </form>
        </Card>
      )}

      {/* 5. PRESUPUESTO CLIENTE */}
      {stageIdx >= 4 && (
        <Card title="5 · Presupuesto al Cliente" testId="section-presupuesto"
          badge={item.presupuesto_cliente?.aprobado_interno
            ? <span className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-full" style={{ backgroundColor: '#8B9F8D20', color: '#8B9F8D' }}><ShieldCheck size={12} /> Aprobado por {item.presupuesto_cliente.aprobado_por}</span>
            : <span className="text-xs px-3 py-1.5 rounded-full" style={{ backgroundColor: '#D4A57420', color: '#D4A574' }}>Pendiente aprobación interna</span>}>
          <p className="text-xs mb-3 font-light" style={{ color: '#C9B8A8' }}>Costo del carpintero: ${Number(item.cotizacion_carpintero?.monto || 0).toLocaleString()} MXN</p>
          <form onSubmit={e => { e.preventDefault(); saveSection('presupuesto_cliente', forms.presupuesto, 'Presupuesto guardado'); }} className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <Field label="Precio de venta (MXN)"><input data-testid="pres-precio" type="number" step="0.01" required value={forms.presupuesto.precio_venta} onChange={e => setF('presupuesto', { precio_venta: e.target.value })} className={inputCls} style={inputStyle} /></Field>
              <Field label="Condiciones de pago"><input data-testid="pres-condiciones" placeholder="Ej: 50% anticipo, 50% contra entrega" value={forms.presupuesto.condiciones_pago || ''} onChange={e => setF('presupuesto', { condiciones_pago: e.target.value })} className={inputCls} style={inputStyle} /></Field>
              <Field label="Vigencia"><input data-testid="pres-vigencia" placeholder="Ej: 15 días" value={forms.presupuesto.vigencia || ''} onChange={e => setF('presupuesto', { vigencia: e.target.value })} className={inputCls} style={inputStyle} /></Field>
            </div>
            <div className="flex gap-3">
              <SaveBtn saving={saving} testId="pres-save-btn" />
              {!item.presupuesto_cliente?.aprobado_interno && isAdmin && item.presupuesto_cliente?.precio_venta && (
                <button data-testid="pres-approve-btn" type="button" disabled={saving} onClick={() => simpleAction(`/api/furniture/${fid}/presupuesto/approve`, {}, 'Presupuesto aprobado internamente')}
                  className="px-5 py-2.5 rounded-lg text-sm font-medium transition-all disabled:opacity-50" style={{ backgroundColor: '#8B9F8D', color: '#fff' }}>
                  <span className="flex items-center gap-1.5"><ShieldCheck size={14} /> Aprobar internamente</span>
                </button>
              )}
            </div>
          </form>
        </Card>
      )}

      {/* 6. APROBACIÓN CLIENTE */}
      {stageIdx >= 5 && (
        <Card title="6 · Aprobación del Cliente" testId="section-aprobacion"
          badge={item.aprobacion_cliente?.aprobado
            ? <span className="text-xs px-3 py-1.5 rounded-full" style={{ backgroundColor: '#8B9F8D20', color: '#8B9F8D' }}>✓ Aprobado {item.aprobacion_cliente.aprobado_at?.slice(0, 10)}</span>
            : <span className="text-xs px-3 py-1.5 rounded-full" style={{ backgroundColor: '#B87B7B20', color: '#B87B7B' }}>{item.aprobacion_cliente?.invalidada ? 'Aprobación invalidada — reenviar' : 'Sin aprobación'}</span>}>
          <p className="text-sm font-light mb-3" style={{ color: '#2D2D2D' }}>
            El link público de aprobación para el cliente estará disponible próximamente. Mientras tanto, un administrador puede registrar la aprobación recibida (WhatsApp, correo, verbal).
          </p>
          {!item.aprobacion_cliente?.aprobado && isAdmin && (
            <button data-testid="manual-approval-btn" disabled={saving}
              onClick={() => simpleAction(`/api/furniture/${fid}/aprobacion/manual`, { nota: 'Registrada por administrador' }, 'Aprobación del cliente registrada')}
              className="px-5 py-2.5 rounded-lg text-sm font-medium disabled:opacity-50" style={{ backgroundColor: '#2D2D2D', color: '#FAF9F6' }}>
              Registrar aprobación manual
            </button>
          )}
        </Card>
      )}

      {/* 7. ANTICIPO */}
      {stageIdx >= 6 && (
        <Card title="7 · Anticipo" testId="section-anticipo" badge={donePast('anticipo') && <Check size={16} style={{ color: '#8B9F8D' }} />}>
          <form onSubmit={e => { e.preventDefault(); saveSection('anticipo', forms.anticipo, 'Anticipo registrado'); }} className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <Field label="Monto (MXN)"><input data-testid="ant-monto" type="number" step="0.01" required value={forms.anticipo.monto} onChange={e => setF('anticipo', { monto: e.target.value })} className={inputCls} style={inputStyle} /></Field>
              <Field label="Porcentaje (%)"><input data-testid="ant-pct" type="number" step="1" value={forms.anticipo.porcentaje || ''} onChange={e => setF('anticipo', { porcentaje: e.target.value })} className={inputCls} style={inputStyle} /></Field>
              <Field label="Fecha"><input data-testid="ant-fecha" type="date" required value={forms.anticipo.fecha || ''} onChange={e => setF('anticipo', { fecha: e.target.value })} className={inputCls} style={inputStyle} /></Field>
            </div>
            <PhotoUploader furnitureId={fid} photos={forms.anticipo.comprobantes} onChange={comprobantes => setF('anticipo', { comprobantes })} label="Comprobante de pago" accept="image/*,.pdf" testId="ant-comprobantes" />
            <SaveBtn saving={saving} testId="ant-save-btn" />
          </form>
        </Card>
      )}

      {/* 8. FABRICACIÓN */}
      {stageIdx >= 7 && (
        <Card title="8 · Fabricación" testId="section-fabricacion" badge={donePast('fabricacion') && <Check size={16} style={{ color: '#8B9F8D' }} />}>
          <form onSubmit={e => { e.preventDefault(); const { avances, ...rest } = forms.fabricacion; saveSection('fabricacion', rest, 'Fabricación actualizada'); }} className="space-y-4 mb-6">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Fecha de inicio"><input data-testid="fab-inicio" type="date" required value={forms.fabricacion.fecha_inicio || ''} onChange={e => setF('fabricacion', { fecha_inicio: e.target.value })} className={inputCls} style={inputStyle} /></Field>
              <Field label="Entrega estimada"><input data-testid="fab-entrega" type="date" value={forms.fabricacion.fecha_estimada_entrega || ''} onChange={e => setF('fabricacion', { fecha_estimada_entrega: e.target.value })} className={inputCls} style={inputStyle} /></Field>
            </div>
            <SaveBtn saving={saving} testId="fab-save-btn" />
          </form>
          <div className="border-t pt-4" style={{ borderColor: '#E8E3D8' }}>
            <p className="text-xs font-medium tracking-wider uppercase mb-3" style={{ color: '#C9B8A8' }}>Avances</p>
            {(item.fabricacion?.avances || []).map(a => (
              <div key={a.id} className="mb-3 rounded-lg border p-3" style={{ borderColor: '#E8E3D8' }}>
                <p className="text-xs font-light" style={{ color: '#2D2D2D' }}>{a.nota}</p>
                <p className="text-[10px] mt-1" style={{ color: '#C9B8A8' }}>{a.registrado_por} · {a.fecha?.slice(0, 10)}</p>
                {a.fotos?.length > 0 && <div className="flex gap-2 mt-2 flex-wrap">{a.fotos.map(p => p.thumb_url && <img key={p.id} src={`${process.env.REACT_APP_BACKEND_URL}${p.thumb_url}`} alt="" loading="lazy" className="w-16 h-16 rounded-lg object-cover" />)}</div>}
              </div>
            ))}
            <form onSubmit={e => {
              e.preventDefault();
              simpleAction(`/api/furniture/${fid}/fabricacion/avance`, forms.avance, 'Avance registrado').then(() => setF('avance', { nota: '', fotos: [] }));
            }} className="space-y-3">
              <input data-testid="avance-nota" placeholder="Nota del avance..." required value={forms.avance.nota} onChange={e => setF('avance', { nota: e.target.value })} className={inputCls} style={inputStyle} />
              <PhotoUploader furnitureId={fid} photos={forms.avance.fotos} onChange={fotos => setF('avance', { fotos })} label="Fotos del avance" testId="avance-photos" />
              <SaveBtn saving={saving} testId="avance-save-btn">Registrar avance</SaveBtn>
            </form>
          </div>
        </Card>
      )}

      {/* 9. INSTALACIÓN */}
      {stageIdx >= 8 && (
        <Card title="9 · Instalación y Entrega" testId="section-instalacion"
          badge={item.instalacion?.conformidad
            ? <span className="text-xs px-3 py-1.5 rounded-full" style={{ backgroundColor: '#8B9F8D20', color: '#8B9F8D' }}>✓ Recibido de conformidad</span>
            : <span className="text-xs px-3 py-1.5 rounded-full" style={{ backgroundColor: '#D4A57420', color: '#D4A574' }}>Pendiente conformidad</span>}>
          <form onSubmit={e => { e.preventDefault(); saveSection('instalacion', { fotos: forms.instalacion.fotos }, 'Fotos guardadas'); }} className="space-y-4">
            <PhotoUploader furnitureId={fid} photos={forms.instalacion.fotos} onChange={fotos => setF('instalacion', { fotos })} label="Fotos del mueble instalado" testId="inst-photos" />
            <div className="flex gap-3">
              <SaveBtn saving={saving} testId="inst-save-btn" />
              {!item.instalacion?.conformidad && isAdmin && (
                <button data-testid="conformidad-btn" type="button" disabled={saving}
                  onClick={() => simpleAction(`/api/furniture/${fid}/conformidad/manual`, {}, 'Conformidad registrada — mueble entregado')}
                  className="px-5 py-2.5 rounded-lg text-sm font-medium disabled:opacity-50" style={{ backgroundColor: '#6B8F6B', color: '#fff' }}>
                  Registrar "Recibido de conformidad"
                </button>
              )}
            </div>
          </form>
          <p className="text-xs mt-3 font-light" style={{ color: '#C9B8A8' }}>El botón público "Recibido de conformidad" para el cliente estará disponible junto con el link de aprobación.</p>
        </Card>
      )}

      {/* AUDITORÍA */}
      <div className="rounded-xl border" style={{ backgroundColor: '#FFFFFF', borderColor: '#E8E3D8' }}>
        <button data-testid="audit-toggle" onClick={() => setShowAudit(!showAudit)} className="w-full flex items-center justify-between p-5">
          <span className="flex items-center gap-2 text-sm font-light" style={{ color: '#2D2D2D' }}><History size={15} style={{ color: '#C9A66B' }} /> Auditoría ({item.audit_log?.length || 0} eventos)</span>
          <ChevronRight size={15} style={{ color: '#C9B8A8', transform: showAudit ? 'rotate(90deg)' : 'none', transition: 'transform .2s' }} />
        </button>
        {showAudit && (
          <div className="px-5 pb-5 space-y-2">
            {[...(item.audit_log || [])].reverse().map((l, i) => (
              <div key={i} className="flex items-start gap-3 text-xs border-t pt-2" style={{ borderColor: '#F5F3EF' }}>
                <span className="shrink-0 font-mono" style={{ color: '#C9B8A8' }}>{l.at?.slice(0, 16).replace('T', ' ')}</span>
                <span style={{ color: '#2D2D2D' }}><b style={{ color: '#C9A66B' }}>{l.user_name}</b> · {l.details}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
